import { DatePipe } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import * as Highcharts from 'highcharts/highstock';

@Injectable({
  providedIn: 'root',
})
export class FormatService {
  private datePipe = inject(DatePipe);

  private readonly dateFormat = 'd MMM, YYYY';
  private readonly weekFormat = 'd MMM, YYYY';
  private readonly isoWeekFormat = "YYYY-'W'ww";
  private readonly monthFormat = 'MMM YYYY';
  private readonly yearFormat = 'YYYY';
  private readonly hourlyFormat = 'd MMM, YYYY HH';

  constructor() {}

  formatNumber(
    value: number,
    decimals?: number,
    prefix?: string,
    suffix?: string,
  ): string {
    return this.getFormattedNumber(value, decimals, prefix, suffix);
  }

  formatNumberCompact(
    value: number,
    decimals?: number,
    prefix?: string,
    suffix?: string,
  ): string {
    return this.getCompactFormattedNumber(value, decimals, prefix, suffix);
  }

  private getFormattedNumber(
    value: number,
    decimals?: number,
    prefix?: string,
    suffix?: string,
  ): string {
    const absValue = Math.abs(value);
    const prefixVal = prefix ?? '';
    const suffixVal = suffix ?? '';

    // For values between 0 and 1, default to 2 decimal places
    if (absValue < 1 && decimals === undefined) {
      decimals = 2;
    }
    const decimalLength = decimals ?? 0;

    let formattedValue: string;
    formattedValue =
      prefixVal +
      Highcharts.numberFormat(absValue, decimalLength, '.', ' ') +
      suffixVal;
    // Remove trailing zeros after the decimal point
    if (formattedValue.includes('.')) {
      formattedValue = formattedValue.replace(/(\.\d*?)0+$/, '$1');
      formattedValue = formattedValue.replace(/\.$/, '');
    }
    return value < 0 ? '-' + formattedValue : formattedValue;
  }

  private getCompactFormattedNumber(
    value: number,
    decimals?: number,
    prefix?: string,
    suffix?: string,
  ) {
    const absValue = Math.abs(value);
    const prefixVal = prefix ?? '';
    const suffixVal = suffix ?? '';

    // Default decimals for different value ranges
    const millionDecimal = decimals ?? 2;
    const thousandDecimal = decimals ?? 1;
    const unitIntervalDecimal = decimals ?? 2;

    let formattedValue: string;

    if (absValue >= 1e6) {
      formattedValue = (absValue / 1e6).toFixed(millionDecimal);
    } else if (absValue >= 1e3) {
      formattedValue = (absValue / 1e3).toFixed(thousandDecimal);
    } else if (absValue < 1) {
      formattedValue = absValue.toFixed(unitIntervalDecimal);
    } else {
      formattedValue = Highcharts.numberFormat(
        absValue,
        decimals ?? 0,
        '.',
        ' ',
      );
    }

    // Remove trailing zeros after the decimal point
    if (formattedValue.includes('.')) {
      formattedValue = formattedValue.replace(/(\.\d*?)0+$/, '$1');
      formattedValue = formattedValue.replace(/\.$/, '');
    }

    // Add 'M' or 'k' as suffix
    if (absValue >= 1e6) {
      formattedValue += 'M';
    } else if (absValue >= 1e3) {
      formattedValue += 'k';
    }

    return value < 0
      ? '-' + prefixVal + formattedValue + suffixVal
      : prefixVal + formattedValue + suffixVal;
  }

  getJSDate(dateObject): Date {
    const ISO_WEEK_REGEX = /^\d{4}-W\d{2}$/;

    // Check if the object represents a serialized Date object,
    // and convert it to a JS Date. Note: months in JS Date are 0-indexed,
    // while in the serialized format, they are 1-indexed.
    if (typeof dateObject === 'object' && dateObject['^O'] == 'Date') {
      return new Date(dateObject.year, dateObject.month - 1, dateObject.day);
    }
    // If the object has a Unix timestamp ('^t'), convert the timestamp (in seconds)
    // to a JS Date (which expects milliseconds).
    else if (typeof dateObject === 'object' && dateObject['^t']) {
      return new Date(dateObject['^t'] * 1000);
    }
    // If the input is a string representing a year and ISO week (e.g., "2024-W02"),
    // convert it to the corresponding JS Date. ISO week W01 is defined as the week
    // containing the first Thursday of the year, starting from the Monday of that week.
    // To convert to a JS Date:
    // 1. Determine the first day of the year (January 1).
    // 2. Calculate the offset to the first Monday of the year, as ISO weeks start on Mondays.
    //    - If January 1st is a Monday, there's no offset.
    //    - If January 1st is a Sunday, offset by 1 day (to move to Monday).
    //    - For other days, calculate how far the next Monday is.
    // 3. Once we have the first Monday of the year, we add (week - 1) * 7 days
    //    to get to the correct ISO week.
    else if (
      typeof dateObject === 'string' &&
      ISO_WEEK_REGEX.test(dateObject)
    ) {
      const [year, week] = dateObject.split('-W').map(Number);
      const jan1 = new Date(year, 0, 1);

      // Calculate how far the first Monday of the year is from January 1st
      const dayOffset =
        jan1.getDay() === 0 ? 1 : jan1.getDay() === 1 ? 0 : 8 - jan1.getDay();

      // Get the first Monday of the year by adding the offset
      const firstMonday = new Date(year, 0, 1 + dayOffset);

      // Add (week - 1) * 7 days to reach the start of the desired ISO week
      return new Date(
        firstMonday.setDate(firstMonday.getDate() + (week - 1) * 7),
      );
    }
    return new Date(dateObject);
  }

  getFormattedDate(
    date: Date,
    granularity: string,
    iso_week?: boolean,
  ): string {
    let format: string;
    switch (granularity) {
      case 'yearly':
        format = this.yearFormat;
        break;
      case 'monthly':
        format = this.monthFormat;
        break;
      case 'weekly':
        if (iso_week) {
          format = this.isoWeekFormat;
        } else {
          format = this.weekFormat;
        }
        break;
      case 'daily':
        format = this.dateFormat;
        break;
      case 'hourly':
        format = this.hourlyFormat;
        break;
      default:
        throw new Error(
          `Unsupported time-series granularity: "${granularity}"`,
        );
    }
    return this.datePipe.transform(new Date(date), format);
  }
}
